import _ from "lodash";
import { singular } from "pluralize";

export const DEVICE_CLASS_ACTION = "com.k4connect.action";
export const DEVICE_CLASS_ACTION_SERVER = "com.k4connect.server.action";
export const DEVICE_CLASS_CONDITION = "com.k4connect.condition";
export const DEVICE_CLASS_CONTROLLER = "com.k4connect.controller";
export const DEVICE_CLASS_DELAY = "com.k4connect.delay";
export const DEVICE_CLASS_INTERVAL = "com.k4connect.interval";
export const DEVICE_CLASS_OR = "com.k4connect.or";
export const DEVICE_CLASS_ROOM = "com.k4connect.room";
export const DEVICE_CLASS_SCENE = "com.k4connect.scene";
export const DEVICE_CLASS_STEP = "com.k4connect.step";
export const DEVICE_CLASS_TRIGGER = "com.k4connect.trigger";

// Special case  that *doesn't* require provisioning, since all pairing is done
// through a third-party.
export const DEVICE_CLASS_ALEXA = "com.k4connect.alexa";

// Values in a node's `meta().extends` array
// ----------------------------------------------------------------------------
// NOTE: that not all of these values have a 'base' version and only a 'zwave'
// version, so matching based on these values for non-zwave devices will not
// work. This code will need to be revisited once the relevant portions of
// `descriptions.json` is fixed.
// ----------------------------------------------------------------------------
export const CLS_LIGHT = "com.k4connect.base.light";
export const CLS_SENSOR = "com.k4connect.zwave.sensor"; // WARNING: Should be 'base' sensor.
export const CLS_DIMMER = "com.k4connect.base.dimmer";

export const NODE_GROUP_ACTION = "actions";
export const NODE_GROUP_SENSOR = "sensors";

export const VAR_TYPE_COMMAND = "function";
export const VAR_TYPE_EVENT = "event";

// Special datetime types, "offset" and "timezone" are not considered, since
// they are both "string" types, and their behavior needs to be deduced from
// the property path.
export const VAR_TYPES_DATE = ["date", "day", "month", "weekday"];
export const VAR_TYPES_TIME = ["time"];

export const CMD_RESP_ACK = { status: "STATUS-ACK" };
export const isAckResp = resp => _.isEqual(resp, CMD_RESP_ACK);

// Following the following convention for creating new error classes:
// https://developer.mozilla.org/en-US/docs/Web/JavaScript/Reference/Global_Objects/Error#ES6_Custom_Error_Class

/**
 * Error subclass for exceptions thrown while attempting to perform operations
 * against live K4 model.
 */
export class NodeOperationError extends Error {
	constructor(operation, node, ...args) {
		super(...args);

		this.name = "NodeOperationError";
		this.operation = operation;
		this.node = node;

		// Maintains proper stack trace for where our error was thrown
		// (only available in V8)
		if (Error.captureStackTrace) {
			Error.captureStackTrace(this, NodeOperationError);
		}
	}

	toString() {
		let str = `${this.name}: Error performing '${this.operation}' operation`;
		str += this.node ? ` on '${this.node.name}'` : "";
		if (this.message) str += `: ${this.message}`;
		return str;
	}
}

function nodeMeta(node) {
	if (!_.isObject(node)) return null;
	else if (_.isFunction(node.meta)) return node.meta();
	else if (_.isObject(node.meta)) return node.meta;
	else if (_.isObject(node.description && node.description.meta))
		return node.description.meta;
	else return node;
}

export function isVariable(node) {
	if (!_.isObject(node)) return false;

	// "standard" variable definition
	if (!_.isFunction(node.meta)) return false;
	const meta = node.meta() || {};
	const paths = _.get(meta, "paths", []);
	if (_.includes(paths, "variable")) return true;

	// Also check parent, in case this is a nested variable, like a controllers'
	// clock/date, clock/time, etc.
	if (!_.isFunction(node.parent)) return false;
	const parent = node.parent() || {};
	const parentMeta = _.isFunction(parent.meta) ? parent.meta() : {};
	const parentPaths = _.get(parentMeta, "paths", []);
	return _.includes(parentPaths, "variable");
}

export function isProperty(node) {
	if (!_.isObject(node)) return false;

	if (!_.isFunction(node.parent)) return false;
	const parent = node.parent() || {};
	const name = parent.name ? parent.name() : "";

	return name === "properties" || isProperty(parent);
}

function isType(node, types) {
	if (!_.isObject(node)) return false;
	if (_.isNil(node)) return false;

	if (!_.isArray(types)) types = [types];
	const type = _.get(nodeMeta(node), "type");
	return type ? _.includes(types, type) : false;
}

export const isTypeCommand = node => isType(node, VAR_TYPE_COMMAND);
export const isTypeDate = node => isType(node, VAR_TYPES_DATE);
export const isTypeEvent = node => isType(node, VAR_TYPE_EVENT);
export const isTypeTime = node => isType(node, VAR_TYPES_TIME);

// Takes either a vuex or k4model node and returns a human-readable name.
//
// Example - Simple:
// > 'Devices/Room'                           => 'Room'
// > 'Devices/Room/Controller'                => 'Controller'
//
// Example - Nested nodes:
// > 'Devices/Room/Controller/Servers/Action' => 'Action Server'
// > 'Devices/Room/Controller/Bridges/Zwave'  => 'Zwave Bridge'
//
// Example - Simple paths:
// > 'Devices/Office/Z-Wave Dimmer/properties/bridge' => 'Bridge'
export function humanizeNodeName(node) {
	let path = _.isFunction(node.path) ? node.path() : node.path;
	let meta = _.isFunction(node.meta) ? node.meta() : node.meta;
	meta = meta || {};
	let { guid, group } = meta;

	const split = path.split("/");
	let name = _.startCase(_.last(split));
	if (split.length > 3 && guid) {
		group = singular(group);
		name += ` ${group}`;
	}

	return name;
}

// Take a node path and find closest ancestor with a guid
export function closestParentDevice(node) {
	if (!node) return null;

	const root = node.root();
	let parent = node;
	while ((parent = parent.parent())) {
		if (parent === root) {
			parent = null;
			break;
		}

		if (parent.guid()) break;
	}

	return parent;
}

// Get children that are actually defined as valid child groups in the node's
// meta. For example, a node with a type of 'com.k4connect.scene' has defines
// both 'steps' and 'reminders'. So this helper method would only return
// children of those groups, esentially ignoring any events, properties, or
// other children that don't match those groups.
//
// Instead of getting the "valid" groups definition from the node's meta, you
// can pass in your own array as an optional second argument.
//
// Returns an array of nodes.
export function childNodes(node, forceValidGroups) {
	const validGroups = _.isNil(forceValidGroups)
		? (node.meta() || {}).children
		: [].concat(forceValidGroups);
	return _.filter(node.children(), child => {
		const group = _.get(child.meta() || {}, "group");
		return _.includes(validGroups, group);
	});
}

// Like the native k4model rename operation, but update all properties and
// variable values referencing the modified paths in the process.
export async function rename(node, newName) {
	const origPath = node.path(); // Original path before rename
	await node.rename(newName);
	node = node.parent().child(newName); // Refresh node
	const newPath = node.path(); // New path after rename
	return updatePaths(node.root(), origPath, newPath);
}

// Like the native k4model move operation, but update all properties and
// variable values referencing the modified paths in the process.
export async function move(node, destinationPath) {
	const origPath = node.path(); // Original path before move
	await node.parent().move(node.name(), destinationPath);
	node = node
		.root()
		.child(destinationPath)
		.child(node.name()); // Refresh node
	const newPath = node.path(); // New path after rename
	return updatePaths(node.root(), origPath, newPath);
}

export async function updatePaths(model, origPath, newPath) {
	const matches = _.filter(_allPaths(model), ref => {
		const { type } = ref.meta();
		if (type === "path") {
			return (
				ref.value() === origPath || _.startsWith(ref.value(), origPath + "/")
			);
		}
	});

	// Update each matched path reference individually
	for (const match of matches) {
		try {
			const oldVal = match.value();
			const newVal = newPath + oldVal.substr(origPath.length);
			console.debug(`Updating value for node (${match.path()}): ${newVal}`);
			await match.set(newVal);
		} catch (e) {
			// Log failure and continue
			console.debug(
				"Failed to update node value. Node will have broken path reference."
			);
			console.debug(e);
		}
	}
}

// Gather all variables and properties into a single array
function _allPaths(model) {
	// Collect all variable and property references to old path
	const variables = _.get(model.paths, "variable", []);
	const properties = _.chain(model.guids || [])
		.values()
		.map(device => device.child("properties"))
		.compact()
		.flatMap(propertiesNode => _.values(propertiesNode.children()))
		.value();
	return variables.concat(properties);
}

const DEFAULT_DEVICE_ICON = "fa-plug";

export function deviceFAIcon(node) {
	const meta = nodeMeta(node);
	if (_.isNil(meta)) return DEFAULT_DEVICE_ICON;

	if (isTypeTime(node)) return "far fa-clock";
	else if (isTypeDate(node)) return "far fa-calendar-alt";
	else if (isTypeCommand(node))
		// return "far fa-play-circle";
		return "fas fa-lightbulb";
	else if (isTypeEvent(node))
		// return "fas fa-bolt";
		return "fas fa-running";

	const cls = meta.cls;
	switch (cls) {
		case DEVICE_CLASS_CONTROLLER:
			return "fa-satellite-dish";
		case DEVICE_CLASS_DELAY:
		case DEVICE_CLASS_INTERVAL:
			return "fa-hourglass-half";
		case DEVICE_CLASS_ROOM:
			return "fa-door-open";
	}

	const group = meta.group;
	switch (group) {
		case "fans":
			return "fas fa-fan";
		case "lights":
			return "fa-lightbulb";
		case "locks":
			return "fas fa-unlock-alt";
		case "televisions":
			return "fas fa-tv";
		case "thermostats":
			return "fas fa-thermometer-three-quarters";
		case "remotes":
		case "sensors":
		case "voice":
		case "wellness":
		default:
			return "fa-plug";
	}
}
